<template class="customer-service-ratings">
  <v-row
    class="fill-height"
    no-gutters
  >
    <TogglePieChart
      :request-status="requestStatus"
      :series="series"
      :title="title"
      :item-id="itemId"
      :consolidated-scores="consolidatedScores"
      :order-options="orderOptions"
      :displayed-text="displayedText"
      :open-filter="openFilter"
      :has-custom-filter="true"
      class="make-a-copy"
      v-bind="$attrs"
      @reload="getData"
    />
    <v-dialog
      v-model="filterDialog"
      width="50%"
      overlay-color="var(--ds-color-reverse)"
    >
      <v-card>
        <v-toolbar>
          <v-toolbar-title>
            {{ filterTitleLabel }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items class="customer-service-ratings__close-icon">
            <span>
              <StIcon
                class="close-icon"
                icon-name="close"
                @click="filterDialog = false"
              />
            </span>
          </v-toolbar-items>
        </v-toolbar>

        <v-row
          no-gutters
          class="mt-5 mx-5"
        >
          <MultiSelectFilter
            v-model="selectedOperators"
            class="mr-3"
            :select-items="getRepliers"
            item-value="id"
            :label="$t('components.OperatorsLogs.replier')"
            :searchable="true"
          />
        </v-row>

        <v-row
          no-gutters
          class="pb-5 px-5"
        >
          <v-spacer />
          <v-spacer />
          <v-btn
            v-if="hasCustomFilterApplied"
            outlined
            rounded
            class="mr-3"
            @click="clearFilter"
          >
            {{ $t('components.OperatorsLogs.trashLabel') }}
          </v-btn>
          <v-btn
            outlined
            rounded
            @click="applyFilter"
          >
            {{ $t('components.OperatorsLogs.filterLabel') }}
          </v-btn>
        </v-row>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import TogglePieChart from '@/components/cards/models/TogglePieChart';
import ratingModel from '@/mixins/rating-model';
import { mapActions, mapState, mapGetters } from 'vuex';
import EventBus from '@/utils/event-bus';
import WarRoomService from '@/services/WarRoomService';
import repliersModel from '@/mixins/repliers-model';
import MultiSelectFilter from '@/components/filter/MultiSelectFilter.vue';

export default {
  name: 'ConsolidatedCSATPieChart',
  components: {
    TogglePieChart,
    MultiSelectFilter,
  },
  mixins: [
    ratingModel,
    repliersModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: {},
      axesSeriesName: [],
      consolidatedScores: null,
      componentFilter: null,
      selectedOperators: [],
      orderOptions: this.CSAT_RATING,
      filterDialog: false,
      hasCustomFilterApplied: false,
    };
  },
  computed: {
    displayedText() {
      return this.$t('components.ConsolidatedCSATPieChart.csatText');
    },
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    ...mapState('dashboard', [
      'isStaging',
    ]),
    filterTitleLabel() {
      return `Filtro - ${this.title}`;
    },
    series() {
      if (this.rawSeries) {
        return this.axesSeriesName.map(serieName => {
          const serie = this.rawSeries[serieName];
          return {
            color: this.getRatingColor(serieName),
            name: this.getRatingLabel(serieName),
            y: serie,
          };
        });
      }
      return [];
    },
    getRepliers() {
      return this.repliersToSelectItem(this.activeDashboardFilters?.repliers);
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getSeStudioEndpointService',
      'getWarroomService',
    ]),
    async getFilterId() {
      const sacRepliers = this.selectedOperators ? this.selectedOperators.value : null;
      const newCardFilter = {
        sac_replier: {
          value: sacRepliers,
        },
        is_sac: true,
      };
      return WarRoomService.encodeFilter({
        account_id: this.accountId,
        is_staging: this.isStaging,
        filter: JSON.stringify(newCardFilter),
      });
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.applyFilter(options);
    },
    async applyFilter(options) {
      this.requestStatus = 'loading';
      this.componentFilter = await this.getFilterId();
      this.getSeStudioEndpointService({
        itemId: this.itemId,
        servicePath: 'sacSurveysScores',
        serviceParams: {
          filterId: this.componentFilter,
        },
      })
        .then(response => {
          const normalizedResponse = response;
          this.axesSeriesName = normalizedResponse.axes;
          this.rawSeries = this.normalizeRatingFromSeries(normalizedResponse.scores);
          this.consolidatedScores = this.consolidateScores(normalizedResponse.percentSatisfiedCsat, normalizedResponse.averageScore);
          this.requestStatus = 'success';
          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
      if (this.selectedOperators) {
        this.hasCustomFilterApplied = true;
      }
      this.filterDialog = false;
    },
    consolidateScores(percentage, average) {
      return {
        percentScore: percentage.toFixed(2),
        averageScore: average.toFixed(2),
      };
    },
    openFilter() {
      this.filterDialog = true;
    },
    clearFilter() {
      this.selectedOperators = null;
      this.hasCustomFilterApplied = false;
      this.applyFilter();
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-service-ratings {
  &__close-icon {
    align-items: center;
  }
}

::v-deep {
  .customer-service-ratings {
    &__table-truncate {
      td {
        max-width: 10vw;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &__modal-style {
      * {
        color: var(--ds-color-snow) !important;
      }

      h3 {
        font-weight: var(--ds-font-weight-bold);
      }
    }

    &__v-dialog {
      overflow-x: hidden;
    }
  }
}
</style>
